import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  BookValidation,
  ScenarioResult,
  TestDataRun,
  TestDataScenario,
  deleteRun,
  fetchRuns,
  fetchTestDataAvailability,
  runScenario,
  sweepOrphans,
  validateSourceBook,
} from './services/TestData.service';

// Dev-only fixture screen: seed release/print-batch scenarios by cloning a real book, tear a
// whole run down (Firestore + Auth) in one click. Server 401s everything in production.

const SCENARIOS: Array<{ key: TestDataScenario; title: string; blurb: string; defaultCount: number }> = [
  {
    key: 'class_pool',
    title: 'Class Shipment pool',
    blurb:
      'Real classroom: teacher + students (login-able), one cloned print-ready book per student, and a $0 pooled order for each. Log in as the teacher and send the Class Shipment end to end.',
    defaultCount: 6,
  },
  {
    key: 'ready_pool',
    title: 'Ready-to-dispatch pool',
    blurb:
      'Orders already at ready-to-dispatch with cloned print PDFs — select them in All Orders and walk a print job through create → dispatch → callback. With SHIPSTATION_TEST_TAG_ID configured they become REAL ShipStation orders (item name says DO NOT PRINT + test tag; deleted with the run); otherwise they carry a fake testdata-… provider id and ShipStation is untouched.',
    defaultCount: 3,
  },
];

const copy = (value: string, label: string) => {
  navigator.clipboard.writeText(value).then(
    () => toast.success(`${label} copied`),
    () => toast.error('Copy failed'),
  );
};

const TestData: React.FC = () => {
  // Self-gating: rendered inside Settings → Testing for every admin, but the tool only exists
  // where the SERVER says so (FIREBASE_MODE !== production). Hidden entirely elsewhere.
  const [enabled, setEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    fetchTestDataAvailability().then(setEnabled).catch(() => setEnabled(false));
  }, []);

  const [sourceBookId, setSourceBookId] = useState('');
  const [validation, setValidation] = useState<BookValidation | null>(null);
  const [validating, setValidating] = useState(false);
  const [counts, setCounts] = useState<Record<TestDataScenario, number>>({ class_pool: 6, ready_pool: 3 });
  const [running, setRunning] = useState<TestDataScenario | null>(null);
  const [result, setResult] = useState<ScenarioResult | null>(null);
  const [runs, setRuns] = useState<TestDataRun[]>([]);
  const [busyRun, setBusyRun] = useState<string | null>(null);
  const [sweeping, setSweeping] = useState(false);

  const loadRuns = () => {
    fetchRuns().then(setRuns).catch(() => {});
  };
  useEffect(loadRuns, []);

  const handleValidate = async () => {
    if (!sourceBookId.trim()) return;
    setValidating(true);
    try {
      setValidation(await validateSourceBook(sourceBookId.trim()));
    } finally {
      setValidating(false);
    }
  };

  const handleRun = async (scenario: TestDataScenario) => {
    if (!sourceBookId.trim()) {
      toast.error('Paste the id of a finished book to clone first');
      return;
    }
    setRunning(scenario);
    setResult(null);
    try {
      const res = await runScenario(scenario, sourceBookId.trim(), counts[scenario]);
      if (res?.runId && res?.teacher) {
        setResult(res);
        toast.success(res.message || 'Scenario seeded');
      } else {
        toast.error(res?.message || res?.error || 'Seeding failed');
      }
      loadRuns();
    } finally {
      setRunning(null);
    }
  };

  const handleDelete = async (run: TestDataRun) => {
    if (!window.confirm(`Delete run ${run.id}? This removes its accounts (incl. Auth), classroom, books and orders.`)) return;
    setBusyRun(run.id);
    try {
      const res = await deleteRun(run.id);
      res?.ok ? toast.success(res.message || 'Run deleted') : toast.error(res?.error || res?.message || 'Delete failed');
      if (result?.runId === run.id) setResult(null);
      loadRuns();
    } finally {
      setBusyRun(null);
    }
  };

  const handleSweep = async () => {
    setSweeping(true);
    try {
      const res = await sweepOrphans();
      res?.ok ? toast.success(res.message || 'Swept') : toast.error(res?.error || 'Sweep failed');
      loadRuns();
    } finally {
      setSweeping(false);
    }
  };

  if (!enabled) {
    return null; // production (or still checking) — the tool doesn't exist here
  }

  return (
    <div className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3 text-sm">
        Dev-only tool: seeds real, login-able test data by cloning an existing book. Disabled in production. Everything a
        run creates is tracked and deletable below — clean up after yourself.
      </div>

      {/* Source book */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold mb-1">Source book to clone</h2>
        <p className="text-sm text-gray-500 mb-4">
          Clones reference the source's images and print PDF, so PDF generation and printing work on real files. Pick any
          finished book (yours or a customer's) and validate it first.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={sourceBookId}
            onChange={(e) => {
              setSourceBookId(e.target.value);
              setValidation(null);
            }}
            placeholder="bookId of a finished book"
            className="border border-gray-300 rounded px-2 py-1.5 text-sm w-96 max-w-full font-mono"
          />
          <button
            type="button"
            onClick={handleValidate}
            disabled={validating || !sourceBookId.trim()}
            className="bg-storyblue text-white rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
          >
            {validating ? 'Checking…' : 'Validate'}
          </button>
          {validation && (
            <span
              className={`text-sm px-2 py-1 rounded ${validation.ok ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
            >
              {validation.ok
                ? `✓ ${validation.title} — print-ready`
                : validation.error || `Not usable: ${(validation.reasons || []).join(', ') || 'not print-ready'}`}
            </span>
          )}
        </div>
      </div>

      {/* Scenarios */}
      <div className="grid md:grid-cols-2 gap-4">
        {SCENARIOS.map((s) => (
          <div key={s.key} className="bg-white rounded-lg shadow-md p-6 flex flex-col">
            <h2 className="text-lg font-semibold mb-1">{s.title}</h2>
            <p className="text-sm text-gray-500 flex-1">{s.blurb}</p>
            <div className="flex items-center gap-2 mt-4">
              <label className="text-sm text-gray-600">Count</label>
              <input
                type="number"
                min={1}
                max={30}
                value={counts[s.key]}
                onChange={(e) => setCounts((prev) => ({ ...prev, [s.key]: Number(e.target.value) || s.defaultCount }))}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm w-20"
              />
              <button
                type="button"
                onClick={() => handleRun(s.key)}
                disabled={running !== null || !sourceBookId.trim()}
                className="ml-auto bg-storyblue text-white rounded px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
              >
                {running === s.key ? 'Seeding…' : 'Run'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Result */}
      {result?.teacher && (
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-400">
          <h2 className="text-lg font-semibold mb-2">Seeded — run {result.runId}</h2>
          <p className="text-sm text-gray-600 mb-3">{result.message}</p>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-semibold text-gray-700 mb-1">Teacher login</div>
              <div className="flex items-center gap-2 font-mono">
                {result.teacher.email}
                <button className="text-storyblueButton" onClick={() => copy(result.teacher!.email, 'Email')}>copy</button>
              </div>
              <div className="flex items-center gap-2 font-mono">
                {result.teacher.password}
                <button className="text-storyblueButton" onClick={() => copy(result.teacher!.password, 'Password')}>copy</button>
              </div>
              {result.classroomId && <div className="mt-2 text-gray-500">Classroom: <span className="font-mono">{result.classroomId}</span></div>}
              <div className="text-gray-500">Orders: <span className="font-mono">{(result.orderNumbers || []).join(', ')}</span></div>
            </div>
            {!!result.students?.length && (
              <div>
                <div className="font-semibold text-gray-700 mb-1">Students ({result.students.length})</div>
                <div className="max-h-40 overflow-y-auto border rounded divide-y">
                  {result.students.map((st) => (
                    <div key={st.email} className="px-2 py-1 flex justify-between gap-2">
                      <span>{st.name}</span>
                      <span className="font-mono text-gray-500">{st.loginName} / {st.password || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Runs */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Seeded runs</h2>
          <button
            type="button"
            onClick={handleSweep}
            disabled={sweeping}
            className="text-sm text-storyblueButton disabled:opacity-50"
            title="Delete any testdata-prefixed residue not tracked by a run (partial/legacy seeds)"
          >
            {sweeping ? 'Sweeping…' : 'Sweep orphans'}
          </button>
        </div>
        {runs.length === 0 ? (
          <p className="text-sm text-gray-500">No test data runs — everything is clean.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-1 pr-3">Run</th>
                  <th className="py-1 pr-3">Scenario</th>
                  <th className="py-1 pr-3">Teacher</th>
                  <th className="py-1 pr-3">Students</th>
                  <th className="py-1 pr-3">Books</th>
                  <th className="py-1 pr-3">Orders</th>
                  <th className="py-1 pr-3">State</th>
                  <th className="py-1"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {runs.map((run) => (
                  <tr key={run.id}>
                    <td className="py-1.5 pr-3 font-mono">{run.id}</td>
                    <td className="py-1.5 pr-3">{run.scenario === 'class_pool' ? 'Class Shipment pool' : 'Ready-to-dispatch pool'}</td>
                    <td className="py-1.5 pr-3 font-mono">{run.teacherEmail}</td>
                    <td className="py-1.5 pr-3">{run.students}</td>
                    <td className="py-1.5 pr-3">{run.books}</td>
                    <td className="py-1.5 pr-3">{run.orders}</td>
                    <td className="py-1.5 pr-3">
                      {run.failed ? <span className="text-red-600" title={run.error || ''}>failed mid-run</span> : 'ok'}
                    </td>
                    <td className="py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => handleDelete(run)}
                        disabled={busyRun === run.id}
                        className="text-red-600 disabled:opacity-50"
                      >
                        {busyRun === run.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestData;
